import { Guid } from "guid-typescript";
import { CostType, FundingSourceType, ProjectBudgetType } from "./common";
import { HttpParams } from '@angular/common/http';
import { ProjectBudgetStatus } from "../enums/project-budget-status";
import { ProjectBudgetAction, ProjectBudgetStateMachine } from "../states/project-budget.machine";
import { ProjectStatus } from "../enums/project-status";
import { ProjectAction, ProjectStateMachine } from "../states/project.machine";
import { ProjectReportAction, ProjectReportStateMachine } from "../states/project-report.machine";
import { ProjectReportStatus } from "../enums/project-report-status";

export interface Project {
    id: Guid;
    submissionId: Guid;
    type: ProjectType;
    status: ProjectStatus;
    name: string;
    approvedAmount: number;
    year: number;
    createdAt: Date;
  }

export class ProjectSummary {
  id: Guid;
  type: ProjectType;
  status: ProjectStatus;
  name: string;
  grantName: string;
  organizationName: string;
  approvedAmount: number;
  year: number;
  createdAt: Date;
  startedAt: Date;
  completedAt: Date;
  isInitialBudgetInSync: boolean;
  isInitialBudgetApproved: boolean;
  hasReallocations: boolean;
  areReallocationBudgetsInSync: boolean;
  areReallocationBudgetsApproved: boolean;
  contractCreated: boolean;
  reportsApproved: boolean;
  hasRefundItems: boolean;
  refundCreated: boolean;

  constructor(
    id: Guid,
    type: ProjectType,
    status: ProjectStatus,
    name: string,
    grantName: string,
    organizationName: string,
    approvedAmount: number,
    year: number,
    createdAt: Date,
    startedAt: Date,
    completedAt: Date,
    isInitialBudgetInSync: boolean,
    isInitialBudgetApproved: boolean,
    hasReallocations: boolean,
    areReallocationBudgetsInSync: boolean,
    areReallocationBudgetsApproved: boolean,
    contractCreated: boolean,
    reportsApproved: boolean,
    hasRefundItems: boolean,
    refundCreated: boolean
  ) {
    this.id = id;
    this.type = type;
    this.status = status;
    this.name = name;
    this.grantName = grantName;
    this.organizationName = organizationName;
    this.approvedAmount = approvedAmount;
    this.year = year;
    this.createdAt = createdAt;
    this.startedAt = startedAt;
    this.completedAt = completedAt;
    this.isInitialBudgetInSync = isInitialBudgetInSync;
    this.isInitialBudgetApproved = isInitialBudgetApproved;
    this.hasReallocations = hasReallocations;
    this.areReallocationBudgetsInSync = areReallocationBudgetsInSync;
    this.areReallocationBudgetsApproved = areReallocationBudgetsApproved;
    this.contractCreated = contractCreated;
    this.reportsApproved = reportsApproved;
    this.hasRefundItems = hasRefundItems;
    this.refundCreated = refundCreated;
  }

  get userFriendlyStatus(): string {
    switch (this.status) {
      case ProjectStatus.Preparation: return 'U pripremi';
      case ProjectStatus.Started:     return 'U provedbi';
      case ProjectStatus.Completed:   return 'Završen';
      default:                        return 'Nepoznato';
    }
  }

  isActionAllowed(action: ProjectAction): boolean {
    return ProjectStateMachine.can(action, this.status);
  }

  static fromJson(data: any): ProjectSummary {
    return new ProjectSummary(
      Guid.parse(data.id),
      data.type,
      data.status,
      data.name,
      data.grantName,
      data.organizationName,
      data.approvedAmount,
      data.year,
      data.createdAt,
      data.startedAt,
      data.completedAt,
      data.isInitialBudgetInSync,
      data.isInitialBudgetApproved,
      data.hasReallocations,
      data.areReallocationBudgetsInSync,
      data.areReallocationBudgetsApproved,
      data.contractCreated,
      data.reportsApproved,
      data.hasRefundItems,
      data.refundCreated
    );
  }
}

  export enum ProjectType {
    Allocation = 1,
    Application = 2
  }

  export interface ChangeProjectStatus {
    targetStatus: ProjectStatus;
  }

  export interface CreateProject  {
    submissionId: string;
    name: string;
    approvedAmount: number;
  }

  export interface UpdateProject {
    name: string;
    approvedAmount: number;
  }

  export interface ProjectActivity {
    id: Guid;
    name: string;
    description: string;
    results: string;
    startDate: Date;
    endDate: Date;
  }

  export interface CreateDescriptionReport {
    reportingPeriodStart?: Date;
    reportingPeriodEnd?: Date;
    contractClass?: string;
    organizationName?: string;
    programName?: string;
    authorizedPerson?: string;
    programManager?: string;
    implementationLocation?: string;
    approvedAmount: number;
    spentAmount: number;
    organizationAddress?: string;
    organizationPhone?: string;
    organizationFax?: string;
    contactPerson?: string;
    contactPhone?: string;
    contactEmail?: string;
    website?: string;
    achievementDescription?: string;
    userImpactDescription?: string;
    numberOfUsers?: number;
    deviationReason?: string;
    projectObstacles?: string;
    numberOfEmployees?: number;
    numberOfVolunteers?: number;
    newEmployees?: boolean;
    volunteerActivities?: string;
    personnelChangesReason?: string;
    deviationFromPlannedBudget?: string;
    spendingChallenges?: string;
    coFinancingDetails?: string;
    successStory?: string;
    mediaCollaborations?: string;
    obstaclesAndLessons?: string;
    plannedActivitiesForNextPeriod?: string;
    attachmentsSummary?: string;
    fileStorageLocation?: string;
    activities: ProjectActivity[];
  }

  export interface UpdateDescriptionReport {
    reportingPeriodStart?: Date;
    reportingPeriodEnd?: Date;
    contractClass?: string;
    organizationName?: string;
    programName?: string;
    authorizedPerson?: string;
    programManager?: string;
    implementationLocation?: string;
    approvedAmount?: number;
    spentAmount?: number;
    organizationAddress?: string;
    organizationPhone?: string;
    organizationFax?: string;
    contactPerson?: string;
    contactPhone?: string;
    contactEmail?: string;
    website?: string;
    achievementDescription?: string;
    userImpactDescription?: string;
    numberOfUsers?: number;
    deviationReason?: string;
    projectObstacles?: string;
    numberOfEmployees?: number;
    numberOfVolunteers?: number;
    newEmployees?: boolean;
    volunteerActivities?: string;
    personnelChangesReason?: string;
    deviationFromPlannedBudget?: string;
    spendingChallenges?: string;
    coFinancingDetails?: string;
    successStory?: string;
    mediaCollaborations?: string;
    obstaclesAndLessons?: string;
    plannedActivitiesForNextPeriod?: string;
    attachmentsSummary?: string;
    fileStorageLocation?: string;
    activities: ProjectActivity[];
  }

  export interface DescriptionReport {
    id: Guid;
    projectId: Guid;
    status: ProjectReportStatus;
    reportingPeriodStart: Date;
    reportingPeriodEnd: Date;
    contractClass: string;
    organizationName: string;
    programName: string;
    authorizedPerson: string;
    programManager: string;
    implementationLocation: string;
    approvedAmount: number;
    spentAmount: number;
    organizationAddress: string;
    organizationPhone: string;
    organizationFax: string;
    contactPerson: string;
    contactPhone: string;
    contactEmail: string;
    website: string;
    achievementDescription: string;
    userImpactDescription: string;
    numberOfUsers: number;
    deviationReason: string;
    projectObstacles: string;
    numberOfEmployees: number;
    numberOfVolunteers: number;
    newEmployees: boolean;
    volunteerActivities: string;
    personnelChangesReason: string;
    deviationFromPlannedBudget: string;
    spendingChallenges: string;
    coFinancingDetails: string;
    successStory: string;
    mediaCollaborations: string;
    obstaclesAndLessons: string;
    plannedActivitiesForNextPeriod: string;
    attachmentsSummary: string;
    fileStorageLocation: string;
    submittedAt: Date;
    activities: ProjectActivity[];
  }

  export interface ChangeDescriptionReportStatus {
    targetStatus: ProjectReportStatus;
  }

  export interface ExpenditureItem {
    id: Guid;
    projectBudgetItemId: string;
    type: CostType;
    description: string;
    contractedFundsAmount: number;
    realizedEligibleCosts: number;
    fileStorageLocation?: string;
  }

  export interface CreateExpenditureReport {
    reportingPeriodStart: Date;
    reportingPeriodEnd: Date;
    expenditureItems: ExpenditureItem[];
  }

  export interface UpdateExpenditureReport {
    reportingPeriodStart: Date;
    reportingPeriodEnd: Date;
    expenditureItems: ExpenditureItem[];
  }

  export interface ExpenditureReport {
    id: Guid;
    projectId: Guid;
    status: ProjectReportStatus;
    reportingPeriodStart: Date;
    reportingPeriodEnd: Date;
    submittedAt: Date;
    expenditureItems: ExpenditureItem[];
  }

  export interface ChangeExpenditureReportStatus {
    targetStatus: ProjectReportStatus;
  }

  export enum ReportType {
    Description = 0,
    Expenditure = 1
  }

  export class UnifiedReport {
    id: Guid;
    projectId: Guid;
    type: ReportType;
    name: string;
    status: ProjectReportStatus;
    reportingPeriodStart: Date;
    reportingPeriodEnd: Date;
    submittedAt: Date;

    constructor(
        id: Guid,
        projectId: Guid,
        type: ReportType,
        name: string,
        status: ProjectReportStatus,
        reportingPeriodStart: Date,
        reportingPeriodEnd: Date,
        submittedAt: Date
    ) {
        this.id = id; 
        this.projectId = projectId;
        this.type = type;
        this.name = name;
        this.status = status;
        this.reportingPeriodStart = reportingPeriodStart;
        this.reportingPeriodEnd = reportingPeriodEnd;
        this.submittedAt = submittedAt;
    }

    get userFriendlyStatus(): string {
        switch (this.status) {
            case ProjectReportStatus.Draft:
                return 'Skica';
            case ProjectReportStatus.Submitted:
                return 'Izvještaj predan';
            case ProjectReportStatus.Returned:
                return 'Izvještaj vraćen na doradu';
            case ProjectReportStatus.Resubmitted:
                return 'Izvještaj ponovno predan';
            case ProjectReportStatus.Approved:
                return 'Izvještaj odobren';
            case ProjectReportStatus.Rejected:
                return 'Izvještaj odbijen';
            default:
                return 'Nepoznat status';
        }
    }

    isActionAllowed(action: ProjectReportAction): boolean {
      return ProjectReportStateMachine.can(action, this.status);
    }

    public static fromJson(json: any, type: ReportType, name: string): UnifiedReport {
        return new UnifiedReport(
            json.id ? Guid.parse(json.id) : Guid.createEmpty(),
            json.projectId ? Guid.parse(json.projectId) : Guid.createEmpty(),
            type,
            name,
            json.status ?? ProjectReportStatus.Draft,
            json.reportingPeriodStart,
            json.reportingPeriodEnd,
            json.submittedAt
        );
    }
}

export interface ProjectSaldo {
  total: number;
  hasSavings: boolean;
  hasOverruns: boolean;
  isBalanced: boolean;
  items: ProjectSaldoItem[];
}

export interface ProjectSaldoItem {
  id: string;
  description: string;
  contractedAmount: number;
  realizedAmount: number;
  saldo: number;
  hasSavings: boolean;
  hasOverrun: boolean;
  isBalanced: boolean;
}

export interface Contract {
  id: string;
  name: string;
  classification: string;
  number: string;
  refNumber: string;
  account: string;
  activity: string;
  date: Date;
  //Allocation only
  decisionClassificationCode: string;
  decisionReferenceNumber: string;
  decisionDate: Date;
}


export interface ContractSummary {
  id: string;
  name: string;
  classification: string;
  number: string;
  refNumber: string;
  date: Date;
  //Allocation only
  decisionClassificationCode: string;
  decisionReferenceNumber: string;
  decisionDate: Date;
}


export interface CreateContract {
  name: string;
  classification: string;
  number: string;
  refNumber: string;
  account: string;
  activity: string;
  date: Date;
  //Allocation only
  decisionClassificationCode: string;
  decisionReferenceNumber: string;
  decisionDate: Date;
}

export interface UpdateContract {
  name: string;
  classification: string;
  number: string;
  refNumber: string;
  account: string;
  activity: string;
  date: Date;
  //Allocation only
  decisionClassificationCode: string;
  decisionReferenceNumber: string;
  decisionDate: Date;
}

export interface RefundItem {
  expenditureItemId: string;
  description: string;
  contractedAmount: number;
  realizedAmount: number;
  refundAmount: number;
}


export interface Refund {
  id: string;
  classificationCode: string;
  referenceNumber: string;
  date: Date;
  refundItems: RefundItem[];
}


export interface RefundSummary {
  id: string;
  classificationCode: string;
  referenceNumber: string;
  date: Date;
}


export interface CreateRefund {
  classificationCode: string;
  referenceNumber: string;
  date: Date;
}

export interface UpdateRefund {
  classificationCode: string;
  referenceNumber: string;
  date: Date;
}


export interface UpdateProjectBudget {
  projectBudgetItems: ProjectBudgetItem[],
  projectFundingSources: ProjectFundingSource[]
}

export interface ProjectBudgetItem {
  id: string;
  budgetItemId: string;
  type: CostType;
  description: string;
  amountRequested: number;
  totalAmount: number;
  explanation: string;
  fileStorageLocation: string;
}

export interface ProjectFundingSource {
  id: Guid;
  type: FundingSourceType;
  amount: number;
}


export interface ChangeProjectBudgetStatus {
   targetStatus: ProjectBudgetStatus;
}

export interface CreateProjectBudgetReallocation {
  sourceProjectBudgetId: string;
}

export class ProjectBudget {
  id: string;
  budgetVersion: number;
  budgetType: ProjectBudgetType;
  status: ProjectBudgetStatus;
  projectBudgetItems: ProjectBudgetItem[];
  projectFundingSources: ProjectFundingSource[];

  constructor() {
    this.id = Guid.EMPTY.toString();
    this.budgetVersion = 1;
    this.budgetType = ProjectBudgetType.InitialCopy;
    this.status = ProjectBudgetStatus.Draft
    this.projectBudgetItems = [];
    this.projectFundingSources = [];
  }

    get userFriendlyStatus(): string {
      switch (this.status) {
        case ProjectBudgetStatus.Draft:
          return 'Skica';
        case ProjectBudgetStatus.Submitted:
          return 'Proračun predložen';
        case ProjectBudgetStatus.Returned:
          return 'Proračun vraćen na doradu';
        case ProjectBudgetStatus.Resubmitted:
          return 'Proračun ponovno predložen';
        case ProjectBudgetStatus.Approved:
          return 'Proračun odobren';
        case ProjectBudgetStatus.Rejected:
            return 'Proračun odbijen';
        default:
          return 'Nepoznat status';
      }
    }

    isActionAllowed(action: ProjectBudgetAction): boolean {
       return ProjectBudgetStateMachine.can(action, this.status);
    }

  get totalDirectCostsRequested(): number {
    return this.projectBudgetItems
      .filter((item) => item.type === CostType.Direct)
      .reduce((acc, item) => acc + (item.amountRequested || 0), 0);
  }

  get totalDirectCostsActual(): number {
    return this.projectBudgetItems
      .filter((item) => item.type === CostType.Direct)
      .reduce((acc, item) => acc + (item.totalAmount || 0), 0);
  }

  get totalIndirectCostsRequested(): number {
    return this.projectBudgetItems
      .filter((item) => item.type === CostType.Indirect)
      .reduce((acc, item) => acc + (item.amountRequested || 0), 0);
  }

  get totalIndirectCostsActual(): number {
    return this.projectBudgetItems
      .filter((item) => item.type === CostType.Indirect)
      .reduce((acc, item) => acc + (item.totalAmount || 0), 0);
  }

  get totalCostsRequested(): number {
    return this.totalDirectCostsRequested + this.totalIndirectCostsRequested;
  }

  get totalCostsActual(): number {
    return this.totalDirectCostsActual + this.totalIndirectCostsActual;
  }

  get totalFundingFromOtherSources(): number {
    return this.projectFundingSources.reduce(
      (acc, source) => acc + (source.amount || 0),
      0
    );
  }

  get totalFunding(): number {
    return this.totalCostsRequested + this.totalFundingFromOtherSources;
  }

  public isSynced(projectApprovedAmount: number): boolean {
     return this.totalCostsRequested === projectApprovedAmount && this.totalFunding === this.totalCostsActual;
  }

  public canCreateReallocation(allBudgets: ProjectBudget[]): boolean {
      // Check if there are any non-approved or non-rejected reallocations (Draft, Submitted, Returned, Resubmitted)
      const hasPendingReallocations = allBudgets.some(b => 
          b.budgetType === ProjectBudgetType.Reallocation && 
          b.status !== ProjectBudgetStatus.Approved && 
          b.status !== ProjectBudgetStatus.Rejected
      );
      
      // Cannot create new reallocation if there are pending reallocations
      if (hasPendingReallocations) {
          return false;
      }      
      
       // Find the last approved budget (highest version among all approved budgets)
       const latestApprovedBudget = allBudgets
        .filter(b => b.status === ProjectBudgetStatus.Approved)
        .sort((a, b) => b.budgetVersion - a.budgetVersion)[0];
        
       // Can only create from the last approved budget (initial or reallocation)
       return this.id === latestApprovedBudget?.id;
  }

  public static fromJson(json: any): ProjectBudget {
    const budget = new ProjectBudget();

    budget.id = json.id || Guid.EMPTY.toString();
    budget.budgetVersion = json.budgetVersion ?? 1;
    budget.budgetType = json.budgetType ?? ProjectBudgetType.InitialCopy;
    budget.status = json.status ?? ProjectBudgetStatus.Draft;

    if (Array.isArray(json.projectBudgetItems)) {
      budget.projectBudgetItems = json.projectBudgetItems.map((item: any) => {
        return { ...item };
      });
    }

    if (Array.isArray(json.projectFundingSources)) {
      budget.projectFundingSources = json.projectFundingSources.map((item: any) => {
        return { ...item };
      });
    }

    return budget;
  }
}


export class GetProjectsFilter {
constructor(
  public municipalityId?: string | null,
  public associationId?: string | null,
  public year?: number | null,
  public grantName?: string | null,
  public type?: ProjectType | null,
  public status?: ProjectStatus | null,
  public isInitialBudgetInSyncFilter?: boolean | null,
  public isInitialBudgetApprovedFilter?: boolean | null,
  public hasReallocationFilter?: boolean | null,
  public areReallocationBudgetsInSyncFilter?: boolean | null,
  public areReallocationBudgetsApprovedFilter?: boolean | null,
  public contractCreatedFilter?: boolean | null,
  public reportsApprovedFilter?: boolean | null,
  public hasRefundItemsFilter?: boolean | null,
  public refundCreatedFilter?: boolean | null
) {}

  toQueryParams(): HttpParams {
    let params = new HttpParams();

    if (this.municipalityId) {
      params = params.set('municipalityId', this.municipalityId);
    }  
    if (this.associationId) { 
      params = params.set('associationId', this.associationId);
    }
    if (this.year) { 
      params = params.set('year', this.year);
    }
    if (this.grantName) {
      params = params.set('grantName', this.grantName)
    }
    if (this.type) {
      params = params.set('type', this.type)
    }
    if (this.status) {
      params = params.set('status', this.status)
    }

    if (this.isInitialBudgetInSyncFilter !== null && this.isInitialBudgetInSyncFilter != undefined) {
      params = params.set('isInitialBudgetInSyncFilter', this.isInitialBudgetInSyncFilter);
    }

    if (this.isInitialBudgetApprovedFilter !== null && this.isInitialBudgetApprovedFilter !== undefined) {
      params = params.set('isInitialBudgetApprovedFilter', this.isInitialBudgetApprovedFilter);
    }

    if (this.hasReallocationFilter !== null && this.hasReallocationFilter !== undefined) {
      params = params.set('hasReallocationFilter', this.hasReallocationFilter);
    }

    if (this.areReallocationBudgetsInSyncFilter !== null && this.areReallocationBudgetsInSyncFilter !== undefined) {
      params = params.set('areReallocationBudgetsInSyncFilter', this.areReallocationBudgetsInSyncFilter);
    }

    if (this.areReallocationBudgetsApprovedFilter !== null && this.areReallocationBudgetsApprovedFilter !== undefined) {
      params = params.set('areReallocationBudgetsApprovedFilter', this.areReallocationBudgetsApprovedFilter);
    }

    if (this.contractCreatedFilter !== null && this.contractCreatedFilter !== undefined) {
      params = params.set('contractCreatedFilter', this.contractCreatedFilter);
    }

    if (this.reportsApprovedFilter !== null && this.reportsApprovedFilter !== undefined) {
      params = params.set('reportsApprovedFilter', this.reportsApprovedFilter);
    }

     if (this.hasRefundItemsFilter !== null && this.hasRefundItemsFilter !== undefined) {
          params = params.set('hasRefundItemsFilter', this.hasRefundItemsFilter);
    }

    if (this.refundCreatedFilter !== null && this.refundCreatedFilter !== undefined) {
          params = params.set('refundCreatedFilter', this.refundCreatedFilter);
    }

    return params;
  }
}
